/**
 * ClimbProfileChart
 *
 * Elevation profile with per-climb average pace for both runners.
 *
 *  - Elevation area: full course profile from resampled altitude_a.
 *  - Pace lines: shown ONLY within climb segments as flat horizontal
 *    segments (constant avg pace for each climb). Null / not drawn
 *    outside climbs.
 *  - "N/A" label centred in each non-climb gap.
 *  - Climb zone coloured bands (category colours).
 *  - Category label at each climb start.
 *
 * X-axis : course distance (km)
 * Left Y : pace min/km — inverted (lower value = faster = higher on chart)
 * Right Y: elevation (m)
 */

import { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

const CATEGORY_FILL = {
  'R-C4': 'rgba(250,204,21,0.20)',
  'R-C3': 'rgba(249,115,22,0.22)',
  'R-C2': 'rgba(239,68,68,0.22)',
  'R-C1': 'rgba(220,38,38,0.27)',
  'R-HC': 'rgba(127,29,29,0.32)',
};

const CATEGORY_STROKE = {
  'R-C4': '#facc15',
  'R-C3': '#f97316',
  'R-C2': '#ef4444',
  'R-C1': '#dc2626',
  'R-HC': '#7f1d1d',
};

function formatPace(v) {
  if (v == null) return '—';
  const m = Math.floor(v);
  const s = Math.round((v - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

function paceTickFormatter(v) {
  if (v == null || isNaN(v)) return '';
  const m = Math.floor(v);
  const s = Math.round((v - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDelta(sec) {
  const abs = Math.abs(sec);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = Math.round(abs % 60);
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const km = (label / 1000).toFixed(2);
  const byKey = Object.fromEntries(payload.map((p) => [p.dataKey, p.value]));
  // Δ Time and length are carried on the data point but not plotted — read the raw row.
  const row = payload[0]?.payload;
  const dt = row?.time_delta_s;   // B − A over this climb: >0 A faster, <0 B faster
  const len = row?.length_m;

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(15,23,42,0.92)',
        border: '1px solid rgba(100,116,139,0.4)',
        borderRadius: 1,
        p: '8px 12px',
        fontSize: '0.75rem',
        lineHeight: 1.8,
      }}
    >
      <Typography variant="caption" color="#94a3b8" display="block">
        km {km}
      </Typography>
      {len != null && (
        <Typography variant="caption" display="block" sx={{ color: '#cbd5e1' }}>
          Climb: {len >= 1000 ? `${(len / 1000).toFixed(2)} km` : `${Math.round(len)} m`}
        </Typography>
      )}
      {byKey.pace_a != null ? (
        <Typography variant="caption" display="block" sx={{ color: COLOR_A }}>
          A avg pace: {formatPace(byKey.pace_a)}
        </Typography>
      ) : (
        <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>
          A pace: N/A
        </Typography>
      )}
      {byKey.pace_b != null ? (
        <Typography variant="caption" display="block" sx={{ color: COLOR_B }}>
          B avg pace: {formatPace(byKey.pace_b)}
        </Typography>
      ) : (
        <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>
          B pace: N/A
        </Typography>
      )}
      {dt != null && (
        <Typography
          variant="caption"
          display="block"
          fontWeight="bold"
          sx={{ color: dt > 5 ? COLOR_A : dt < -5 ? COLOR_B : '#94a3b8' }}
        >
          Δ Time: {formatDelta(dt)}
          {dt > 5 ? ' — A faster' : dt < -5 ? ' — B faster' : ''}
        </Typography>
      )}
      {byKey.altitude != null && (
        <Typography variant="caption" display="block" sx={{ color: '#94a3b8' }}>
          Elevation: {Math.round(byKey.altitude)} m
        </Typography>
      )}
    </Box>
  );
}

export default function ClimbProfileChart({
  resampled = [],
  commonClimbs = null,
  climbsA = [],
  selectedClimb = null,   // { start_distance_m, end_distance_m } — highlights a band
  onClimbClick = null,    // (climb) => void — single-click a climb zone
  checkpoints = null,     // checkpoints for reference lines
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use commonClimbs when available, otherwise climbsA for zone bands
  const climbs = useMemo(() => {
    if (commonClimbs && commonClimbs.length > 0) return commonClimbs;
    return climbsA || [];
  }, [commonClimbs, climbsA]);

  const sortedClimbs = useMemo(
    () => [...climbs].sort((a, b) => a.start_distance_m - b.start_distance_m),
    [climbs]
  );

  // Build chart data from resampled elevation.
  // Decimate to ≤ 600 points for performance.
  // For each point: if it falls within a climb segment, assign that
  // climb's avg_pace for A and B; otherwise null.
  const chartData = useMemo(() => {
    if (!resampled.length) return [];
    const step = Math.max(1, Math.floor(resampled.length / 600));

    return resampled
      .filter((_, i) => i % step === 0)
      .map((r) => {
        const dist = r.course_distance_m;
        const inClimb = sortedClimbs.find(
          (c) => dist >= c.start_distance_m && dist <= c.end_distance_m
        );
        return {
          dist,
          altitude: r.altitude_a ?? null,
          pace_a: inClimb ? (inClimb.a?.avg_pace ?? null) : null,
          pace_b: inClimb ? (inClimb.b?.avg_pace ?? null) : null,
          // Carried for the tooltip only (not plotted).
          time_delta_s: inClimb ? (inClimb.time_delta_s ?? null) : null,
          length_m: inClimb ? (inClimb.length_m ?? null) : null,
        };
      });
  }, [resampled, sortedClimbs]);

  // Pace Y domain: from climb avg paces only, with padding, inverted
  // Start from 0 min/km (theoretical fastest) at top to show full scale
  const paceDomain = useMemo(() => {
    const vals = climbs
      .flatMap((c) => [c.a?.avg_pace, c.b?.avg_pace])
      .filter((v) => v != null && v < 30);
    if (!vals.length) return [20, 0];
    const min = 0; // Fixed minimum (fastest pace) at top
    const max = Math.ceil(Math.max(...vals)) + 2;
    return [max, min]; // inverted: high number at bottom, low at top
  }, [climbs]);

  // Elevation Y domain
  const elevDomain = useMemo(() => {
    const vals = chartData.map((r) => r.altitude).filter((v) => v != null);
    if (!vals.length) return [0, 3000];
    const min = Math.floor(Math.min(...vals) / 50) * 50;
    const max = Math.ceil(Math.max(...vals) / 50) * 50;
    return [min, max];
  }, [chartData]);

  // Non-climb zones for "N/A" labels (only zones > 500 m wide)
  const naZones = useMemo(() => {
    if (!chartData.length) return [];
    const xMax = chartData[chartData.length - 1]?.dist ?? 0;
    const zones = [];
    let pos = 0;
    for (const c of sortedClimbs) {
      if (c.start_distance_m - pos > 500) {
        zones.push({ x1: pos, x2: c.start_distance_m });
      }
      pos = c.end_distance_m;
    }
    if (xMax - pos > 500) {
      zones.push({ x1: pos, x2: xMax });
    }
    return zones;
  }, [sortedClimbs, chartData]);

  // Click on a chart position → find which climb contains it
  const handleChartClick = (data) => {
    if (!onClimbClick || data?.activeLabel == null) return;
    const dist = data.activeLabel;
    const hit = sortedClimbs.find(
      (c) => dist >= c.start_distance_m && dist <= c.end_distance_m
    );
    if (hit) onClimbClick(hit);
  };

  if (!chartData.length) return null;

  const xMax = chartData[chartData.length - 1]?.dist ?? 0;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
        Elevation &amp; Pace Profile
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} display="block" mb={1.5}>
        Pace lines show each runner's average for that climb only. Sections outside climbs are marked N/A.
      </Typography>

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart
          data={chartData}
          margin={isMobile ? { top: 55, right: 5, left: 0, bottom: 0 } : { top: 55, right: 60, left: 0, bottom: 0 }}
          onClick={handleChartClick}
          style={{ cursor: onClimbClick ? 'pointer' : 'default' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} vertical={false} />

          <XAxis
            dataKey="dist"
            type="number"
            domain={[0, xMax]}
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}`}
            label={{ value: 'km', position: 'insideBottomRight', offset: -4, fill: colors.grey[400], fontSize: 11 }}
            stroke={colors.grey[500]}
            tick={{ fill: colors.grey[400], fontSize: 11 }}
          />

          {/* Left Y: pace (inverted — faster pace = higher on chart) */}
          <YAxis
            yAxisId="pace"
            orientation="left"
            domain={paceDomain}
            allowDataOverflow={true}
            tickFormatter={paceTickFormatter}
            stroke={colors.grey[500]}
            tick={isMobile ? false : { fill: colors.grey[400], fontSize: 10 }}
            axisLine={!isMobile}
            tickLine={!isMobile}
            width={isMobile ? 0 : 42}
            label={isMobile ? undefined : { value: 'min/km', angle: -90, position: 'insideLeft', offset: 10, fill: colors.grey[400], fontSize: 10 }}
          />

          {/* Right Y: elevation */}
          <YAxis
            yAxisId="elev"
            orientation="right"
            domain={elevDomain}
            tickFormatter={(v) => `${Math.round(v)}`}
            stroke={colors.grey[500]}
            tick={isMobile ? false : { fill: colors.grey[400], fontSize: 10 }}
            width={isMobile ? 0 : 45}
            axisLine={!isMobile}
            tickLine={!isMobile}
            label={isMobile ? undefined : { value: 'm', angle: 90, position: 'insideRight', offset: 10, fill: colors.grey[400], fontSize: 10 }}
          />

          {/* Climb zone coloured bands */}
          {sortedClimbs.map((c, i) => {
            const isSelected =
              selectedClimb != null &&
              Math.abs(c.start_distance_m - selectedClimb.start_distance_m) < 1;
            return (
              <ReferenceArea
                key={`band-${i}`}
                yAxisId="elev"
                x1={c.start_distance_m}
                x2={c.end_distance_m}
                fill={
                  isSelected
                    ? (CATEGORY_STROKE[c.category] || '#94a3b8') + '55'
                    : CATEGORY_FILL[c.category] || 'rgba(148,163,184,0.15)'
                }
                stroke={CATEGORY_STROKE[c.category] || '#94a3b8'}
                strokeWidth={isSelected ? 2.5 : 1}
                strokeOpacity={isSelected ? 1 : 0.7}
                ifOverflow="visible"
              />
            );
          })}

          {/* Category label at each climb start */}
          {sortedClimbs.map((c, i) => (
            <ReferenceLine
              key={`cat-${i}`}
              yAxisId="elev"
              x={c.start_distance_m}
              stroke="none"
              label={{
                value: c.category || '',
                position: 'insideTopRight',
                fill: CATEGORY_STROKE[c.category] || '#94a3b8',
                fontSize: 9,
                fontWeight: 'bold',
              }}
            />
          ))}

          {/* Checkpoint reference lines with labels */}
          {checkpoints?.map((cp, i) => (
            <ReferenceLine
              key={`checkpoint-${i}`}
              yAxisId="elev"
              x={cp.distance_m}
              stroke={colors.greenAccent[500]}
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: cp.name,
                position: 'top',
                fill: colors.greenAccent[500],
                fontSize: 11,
                angle: -45,
                offset: 5,
              }}
            />
          ))}

          {/* N/A labels in non-climb zones */}
          {naZones.map((z, i) => (
            <ReferenceArea
              key={`na-${i}`}
              yAxisId="pace"
              x1={z.x1}
              x2={z.x2}
              fill="transparent"
              stroke="none"
              label={{
                value: 'N/A',
                position: 'center',
                fill: colors.grey[600],
                fontSize: 11,
                fontStyle: 'italic',
              }}
            />
          ))}

          {/* Elevation filled area */}
          <Area
            yAxisId="elev"
            type="monotone"
            dataKey="altitude"
            stroke={colors.grey[500]}
            strokeWidth={1}
            fill={colors.grey[800]}
            fillOpacity={0.6}
            dot={false}
            activeDot={false}
            name="Elevation"
            legendType="none"
          />

          {/* Pace lines — only drawn within climbs (null outside) */}
          <Line
            yAxisId="pace"
            type="stepAfter"
            dataKey="pace_a"
            stroke={COLOR_A}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4 }}
            name="A avg pace"
            connectNulls={false}
          />
          <Line
            yAxisId="pace"
            type="stepAfter"
            dataKey="pace_b"
            stroke={COLOR_B}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4 }}
            name="B avg pace"
            connectNulls={false}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '0.75rem' }}
            formatter={(value) => <span style={{ color: colors.grey[200] }}>{value}</span>}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
